<template>
  <div class="row mx-0 px-3 py-4 border-top">
    <div
      class="d-flex pe-0 justify-content-between justify-content-md-start align-items-center mb-1"
    >
      <label class="size-12" for="">希望職種</label>
      <span class="tag-thirdly size-10 ms-2">必須</span>
    </div>
    <div class="input-group d-flex w-100 pe-0 gap-3 flex-column">
      <div
        v-for="(occupation, index) in optionalInformationStore.desired_occupations"
        :key="index"
        class="d-flex w-100 gap-3"
      >
        <div class="pe-0 col-10 col-md-4" :class="index > 0 ? 'col-10' : 'col-12'">
          <Field
            v-slot="{ field, errors }"
            v-model="occupation.occupation_id"
            :name="`desired-occupation${index}`"
          >
            <select
              class="form-select select-input-primary size-14 p-0d8 pe-4"
              v-bind="field"
              @change="onChangeOccupationId($event, occupation, index)"
            >
              <option :value="0" disabled selected>選択してください</option>
              <optgroup
                v-for="occupationGroup in filteredOccupations(index)"
                :key="occupationGroup.id"
                :label="occupationGroup.name"
              >
                <option v-for="item in occupationGroup.children" :key="item.id" :value="item.id">
                  {{ item.name }}
                </option>
              </optgroup>
            </select>
            <div class="d-block invalid-feedback">{{ errors[0] }}</div>
          </Field>
        </div>
        <div class="d-flex justify-content-center align-items-center flex-column ms-2">
          <img
            alt="Remove icon"
            class="h-29x w-29x cursor-pointer"
            src="@/assets/images/icons/remove-icon.svg"
            @click="onRemoveOccupation(index)"
          />
        </div>
      </div>
      <div v-if="isDesiredOccupationInvalid" class="d-block invalid-feedback">
        {{ desiredOccupationError }}
      </div>
      <div class="d-flex justify-content-center align-items-center flex-column w-100 m-0">
        <button
          v-if="optionalInformationStore.desired_occupations.length < maxItem"
          type="button"
          class="btn btn-outline-secondary p-0d8 d-flex justify-content-center align-items-center size-12 w-12 rounded-5 h-36x"
          @click="addItemForDesiredOccupation"
        >
          <div class="icon w-1"></div>
          <span class="ms-2">希望職種を追加する</span>
        </button>
        <p class="d-flex justify-content-center text-secondary pe-2 pt-1 text-center">※ 3つまで</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, watchEffect, computed } from 'vue';
import { getOccupations } from '@/apis/candidate';
import { useOptionalInformationStore } from '@/stores/resume-candidate/OptionalInformation.store';
import { OccupationFourth, OccupationPrimary } from '@/types/models/occupation';
import { Field, useFieldArray } from 'vee-validate';
import { useFormErrorsStore } from '@/stores/resume-candidate/Errors.store';
import { pullAt } from 'lodash';

const valueOccupations = ref<OccupationFourth[]>([]);
const occupations = ref<OccupationPrimary[]>([]);
const optionalInformationStore = useOptionalInformationStore();
const { update, remove } = useFieldArray('desired_occupations');
const maxItem = 3;
const formErrorsStore = useFormErrorsStore();
const desiredOccupationError = ref('');

onMounted(() => {
  loadOccupations();
  checkStoreLoaded();
});

watchEffect(() => {
  desiredOccupationError.value = formErrorsStore.errors.desired_occupations || '';
});

const loadOccupations = async (): Promise<void> => {
  const res = await getOccupations();
  if (res.status === 200) {
    occupations.value = res.data;
  }
};

const checkStoreLoaded = (): void => {
  if (optionalInformationStore.desired_occupations.length > 0) {
    updateValueOccupations();
  } else {
    const unwatch = watch(
      () => optionalInformationStore.desired_occupations,
      (newValue) => {
        if (newValue.length > 0) {
          updateValueOccupations();
          unwatch();
        }
      }
    );
  }
};

const updateValueOccupations = (): void => {
  valueOccupations.value = [];
  optionalInformationStore.desired_occupations.map((desired_occupation) => {
    valueOccupations.value.push({
      index: new Date().getTime(),
      name: desired_occupation.name,
      occupation_id: desired_occupation.occupation_id,
    });
  });
};

const onRemoveOccupation = (index: number): void => {
  remove(index);
  pullAt(optionalInformationStore.desired_occupations, index);
};

const addItemForDesiredOccupation = (): void => {
  if (optionalInformationStore.desired_occupations.length >= maxItem) return;
  optionalInformationStore.desired_occupations?.push({
    occupation_id: 0,
    index: new Date().getTime(),
  });
};

const onChangeOccupationId = ($event: any, desired_occupation: any, index: number): void => {
  update(index, { ...desired_occupation, occupation_id: $event.target.value });
  const objectIndex = optionalInformationStore.desired_occupations.findIndex(
    (object) => object.index == desired_occupation.index
  );
  optionalInformationStore.desired_occupations[objectIndex].occupation_id = +$event.target.value;
};

const filteredOccupations = (index: number): OccupationPrimary[] => {
  return occupations.value.map((occupation) => {
    const clonedOccupation = { ...occupation };
    clonedOccupation.children = clonedOccupation.children?.filter((child) => {
      return !optionalInformationStore.desired_occupations.some(
        (occupations, i) => occupations.occupation_id === child.id && i !== index
      );
    });
    return clonedOccupation;
  });
};

const isDesiredOccupationInvalid = computed(() => {
  return (
    desiredOccupationError.value &&
    (optionalInformationStore.desired_occupations.length === 0 ||
      optionalInformationStore.desired_occupations[0].occupation_id === 0)
  );
});
</script>
